import { useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { transferBlogOwnership } from '../store/blogSlice';
import type { Blog } from '../types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface TransferOwnershipDialogProps {
  blog: Blog;
  ownerEmail?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const TransferOwnershipDialog = ({
  blog,
  ownerEmail,
  open,
  onOpenChange,
  onSuccess,
}: TransferOwnershipDialogProps) => {
  const dispatch = useAppDispatch();
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenConfirmDialog = () => {
    const email = newOwnerEmail.trim().toLowerCase();

    if (!email) {
      toast.error('Vui lòng nhập email người nhận');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Email không hợp lệ');
      return;
    }

    if (email === ownerEmail?.toLowerCase()) {
      toast.error('Email này đã là owner hiện tại');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleTransfer = async () => {
    const email = newOwnerEmail.trim().toLowerCase();

    setIsSubmitting(true);
    try {
      await dispatch(
        transferBlogOwnership({ blogId: blog.id, email })
      ).unwrap();

      toast.success(`Đã chuyển quyền sở hữu blog cho ${email}`);
      toast.info('Bạn đã không còn là owner của blog này', { duration: 5000 });

      setShowConfirmDialog(false);
      onOpenChange(false);
      setNewOwnerEmail('');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error transferring ownership:', error);
      toast.error(error?.message || 'Không thể chuyển quyền sở hữu');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Main Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Chuyển quyền sở hữu Blog
            </DialogTitle>
            <DialogDescription>
              Chuyển quyền sở hữu blog "{blog.title}" cho người dùng khác.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Warning */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-orange-900 mb-2">
                ⚠️ Cảnh báo quan trọng
              </h4>
              <ul className="text-xs text-orange-800 space-y-1 list-disc list-inside">
                <li>Đây là hành động <strong>không thể hoàn tác</strong></li>
                <li>Bạn sẽ <strong>mất toàn bộ quyền owner</strong> sau khi chuyển</li>
                <li>Bạn sẽ <strong>không tự động trở thành admin</strong></li>
                <li>Owner mới có thể thêm bạn làm admin nếu muốn</li>
              </ul>
            </div>

            {/* Current Owner */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Owner hiện tại</label>
              <Input value={ownerEmail || 'Đang tải...'} disabled className="bg-gray-100" />
            </div>

            {/* New Owner */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Email owner mới <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newOwnerEmail}
                onChange={(e) => setNewOwnerEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSubmitting) {
                    handleOpenConfirmDialog();
                  }
                }}
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">
                Nhập email tài khoản Học Nhé của người sẽ trở thành owner mới
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button
              onClick={handleOpenConfirmDialog}
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Tiếp tục
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Xác nhận chuyển quyền sở hữu?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Bạn sắp chuyển quyền sở hữu blog <strong>"{blog.title}"</strong> cho:
              </p>
              <p className="font-semibold text-gray-900">{newOwnerEmail}</p>
              <p className="text-red-600 font-medium mt-3">
                ⚠️ Sau khi xác nhận, bạn sẽ KHÔNG còn là owner và KHÔNG thể hoàn tác!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              Hủy bỏ
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTransfer}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? 'Đang chuyển...' : 'Xác nhận chuyển quyền'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
